/**
 * Step 3: the photo step. A fan sees their initials plate by default, may
 * pick a photo when the upload path exists, and may skip. `Continue` and
 * `Skip` are identical: both advance `onboardingStep` to "sports". The step
 * never blocks the flow.
 *
 * Four states: the default initials plate, a stored photo with a `Change
 * photo` action, an upload failure banner, and a still-checking banner for a
 * photo that uploaded fine but is not viewable yet (`pendingReview` or
 * `heldOnBudget` from `/upload/complete`). A pending photo never disables or
 * blocks `Continue` or `Skip`.
 *
 * `isUploadAvailable` is passed `true` by this step's one caller,
 * `routes/onboarding.tsx`, because the upload path shipped (ISSUE-285) and
 * object storage is provisioned (ISSUE-461). While false, this step renders
 * no upload control and no file input at all.
 *
 * A picked photo is framed first in the round crop editor, and only the
 * framed square reaches `onFileChosen` (ISSUE-1094).
 */
import { useProfileUpdate } from "@sportsmet/profile-sdk/private";
import { Button, ImageCropEditor, InlineBanner } from "@sportsmet/ui";
import { type ReactNode, useRef, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import {
  CROP_OUTPUT_MAX_EDGE_PX,
  PHOTO_CROP_ASPECT,
  PHOTO_CROP_TITLE,
} from "../imageCrop/imageCropPresets";
import { shouldOpenCropEditor } from "../imageCrop/shouldOpenCropEditor";
import { ProfileAvatar } from "../profile/ProfileAvatar";
import {
  AVATAR_UPLOAD_CONTENT_TYPES,
  MAX_AVATAR_UPLOAD_BYTES,
} from "../profile/useAvatarUpload";
import {
  CONTINUE_LABEL,
  CONTINUE_PENDING_LABEL,
  ONBOARDING_SKIP_LABEL,
  PHOTO_CHANGE_LABEL,
  PHOTO_HEADLINE,
  PHOTO_PENDING_REVIEW_MESSAGE,
  PHOTO_UPLOAD_FAILED_MESSAGE,
  PHOTO_UPLOAD_LABEL,
} from "./onboardingCopy";
import { OnboardingStepFrame } from "./OnboardingStepFrame";

export const PhotoStep = ({
  stepIndex,
  stepTotal,
  displayName,
  photoUrl,
  isUploadAvailable,
  hasUploadFailed,
  uploadFailedMessage = null,
  isPhotoPendingReview = false,
  onFileChosen,
  onAdvanced,
}: {
  /** This fan's position and total, per Q274. Passed straight to OnboardingStepFrame. */
  stepIndex: number;
  stepTotal: number;
  /** The profile's `displayName`, from /profile/get. Drives the initials plate. */
  displayName: string;
  /**
   * The profile's `photoUrl`, from /profile/get. `null` draws the initials
   * plate. The caller NEVER passes a rejected or unpublished image here, which
   * is what makes R4's "never render a rejected image" true by construction.
   */
  photoUrl: string | null;
  /**
   * Passed `true` by `routes/onboarding.tsx`. While false, no upload control
   * and no file input render at all.
   */
  isUploadAvailable: boolean;
  /**
   * True after an upload attempt was refused or failed. Draws the inline
   * failure line. Ignored while `isUploadAvailable` is false.
   */
  hasUploadFailed: boolean;
  /**
   * The refusal sentence `useAvatarUpload` resolved, when it resolved one
   * (ISSUE-839). Null, or absent, falls back to the generic line.
   */
  uploadFailedMessage?: string | null;
  /**
   * True when the fan's photo uploaded correctly but is not viewable yet:
   * /upload/complete answered "pendingReview" or "heldOnBudget". Not a
   * failure and not a rejection. There is no URL to render, so the initials
   * plate stays up. Ignored while `isUploadAvailable` is false. Defaults to
   * false.
   */
  isPhotoPendingReview?: boolean | undefined;
  /**
   * The framed file the fan confirmed in the crop editor, or the picked file
   * when the upload hook would refuse it anyway (ISSUE-1094). The caller owns
   * the upload; this step never calls media-api itself and never holds
   * upload state.
   */
  onFileChosen: (file: File) => void;
  /**
   * Called after /profile/update succeeds, for Continue and for Skip alike.
   * The route raises its ceiling from `reachedServerStep`, which came straight
   * off the update response. This component never navigates.
   */
  onAdvanced: (input: { reachedServerStep: string | null }) => void;
}): ReactNode => {
  const update = useProfileUpdate();
  const isSaving = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  async function handleAdvance(): Promise<void> {
    if (isSaving.current) return;
    isSaving.current = true;

    let result;
    try {
      result = await update.mutateAsync({ onboardingStep: "sports" });
    } catch {
      showApiError({ error: { code: "INTERNAL_CALL_FAILED", message: "" } });
      isSaving.current = false;
      return;
    }

    if (showApiError(result)) {
      isSaving.current = false;
      return;
    }

    onAdvanced({
      reachedServerStep: result.data?.profile.onboardingStep ?? "sports",
    });
  }

  return (
    <OnboardingStepFrame
      stepIndex={stepIndex}
      stepTotal={stepTotal}
      headline={PHOTO_HEADLINE}
    >
      {isUploadAvailable && hasUploadFailed ? (
        <InlineBanner
          variant="warning"
          message={uploadFailedMessage ?? PHOTO_UPLOAD_FAILED_MESSAGE}
        />
      ) : isUploadAvailable && isPhotoPendingReview ? (
        <InlineBanner
          variant="neutral"
          message={PHOTO_PENDING_REVIEW_MESSAGE}
        />
      ) : null}
      <div className="flex flex-col items-center gap-2 py-6">
        <ProfileAvatar
          displayName={displayName}
          photoUrl={photoUrl}
          size="lg"
          labelling="labelled"
          testId="onboarding-photo-avatar"
        />
        {isUploadAvailable && photoUrl !== null ? (
          <Button
            variant="link"
            data-testid="onboarding-photo-change"
            onClick={() => {
              fileInputRef.current?.click();
            }}
          >
            {PHOTO_CHANGE_LABEL}
          </Button>
        ) : null}
      </div>
      {isUploadAvailable && photoUrl === null ? (
        <Button
          variant="secondary"
          fullWidth
          data-testid="onboarding-photo-upload"
          onClick={() => {
            fileInputRef.current?.click();
          }}
        >
          {PHOTO_UPLOAD_LABEL}
        </Button>
      ) : null}
      {isUploadAvailable ? (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="onboarding-photo-file-input"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Clearing lets the same file fire a change again.
              event.target.value = "";
              if (file === undefined) return;
              if (
                shouldOpenCropEditor({
                  file,
                  contentTypes: AVATAR_UPLOAD_CONTENT_TYPES,
                  maxBytes: MAX_AVATAR_UPLOAD_BYTES,
                })
              ) {
                setPendingFile(file);
                return;
              }
              onFileChosen(file);
            }}
          />
          <ImageCropEditor
            file={pendingFile}
            title={PHOTO_CROP_TITLE}
            aspect={PHOTO_CROP_ASPECT}
            shape="round"
            maxOutputEdgePx={CROP_OUTPUT_MAX_EDGE_PX}
            onCancel={() => {
              setPendingFile(null);
            }}
            onConfirm={(cropped) => {
              setPendingFile(null);
              onFileChosen(cropped);
            }}
            testId="onboarding-photo-crop"
          />
        </>
      ) : null}
      <div className="sm-screen-bottom flex flex-col gap-2 pt-2">
        <Button
          fullWidth
          loading={update.isPending}
          loadingLabel={CONTINUE_PENDING_LABEL}
          disabled={false}
          data-testid="onboarding-photo-continue"
          onClick={() => {
            void handleAdvance();
          }}
        >
          {CONTINUE_LABEL}
        </Button>
        <Button
          variant="link"
          fullWidth
          data-testid="onboarding-photo-skip"
          onClick={() => {
            void handleAdvance();
          }}
        >
          {ONBOARDING_SKIP_LABEL}
        </Button>
      </div>
    </OnboardingStepFrame>
  );
};
